<template>
    <div id="orderflow" class="new-styling">
        <router-view />
    </div>
</template>

<script>
    import { mapGetters, mapState } from 'vuex';
    import { getOrderFlowConfig, post } from './plugins/http';
    import { removeBodyScrollLock, scrollToTop } from './utils/global';
    import { trackVirtualPageView } from './utils/tracking';
    import addUserByUserType from './utils/addUserByUserType';
    import { recruitmentCookieName } from './utils/consts';
    import resetOidcStore from './utils/resetOidcStore';

    export default {
        data() {
            return {
                shouldSetAssortments: false,
                isReady: false,
            };
        },
        computed: {
            ...mapState({
                trackingPrefix: state => state.flow.trackingPrefix,
                cartItems: state => state.cart.items,
                currentPageIndex: state => state.flow.currentPageIndex,
                configKey: state => state.config.key,
                currentFlow: state => state.flow.flow,
                flowQueue: state => state.flow.flowQueue,
                orderFlowConfig: state => state.config.orderFlowConfig,
                calculateOrder: state => state.config.orderFlowConfig.endpoints.CalculateOrder,
                users: state => state.cart.users,
                products: state => state.cart.products,
                addons: state => state.cart.addons,
                promoCode: state => state.cart.promoCode,
                currentPages: state => state.config.config.pages,
                activeAssortmentIndex: state => state.products.activeAssortmentIndex,
                assortments: state => state.cart.assortments,
                productList: state => state.products.activeProductList,
                flowType: state => state.flow.flow?.type,
                owner: state => state.cart.owner,
                paymentStatus: state => state.cart.paymentStatus,
                trackingType: state => state.flow.flow?.trackingType,
                tabs: state => state.flow.tabs,
                title: state => state.config.title,
                validationFieldValue: state => state.cart.validationFieldValue,
            }),
            ...mapGetters('cart', [
                'request',
                'assortmentsHash',
            ]),
            isMultiFlow() {
                return this.flowType === 'multi';
            },
        },
        watch: {
            flowType() {
                if (this.isMultiFlow) this.$store.dispatch('cart/setCurrentProductUuid', null);
            },
            cartItems() {
                this.getFlow();
            },
            users() {
                this.shouldSetAssortments = true;
            },
            products() {
                this.shouldSetAssortments = true;
            },
            addons() {
                this.shouldSetAssortments = true;
            },
            promoCode() {
                this.shouldSetAssortments = true;
            },
            owner() {
                this.shouldSetAssortments = true;
            },
            paymentStatus() {
                this.shouldSetAssortments = true;
            },
            validationFieldValue() {
                this.shouldSetAssortments = true;
            },
            shouldSetAssortments() {
                if (!this.isReady) this.shouldSetAssortments = false;
                if (!this.shouldSetAssortments) return;
                this.setAssortments();
                this.shouldSetAssortments = false;
            },
            currentPageIndex() {
                trackVirtualPageView(this.tabs, this.currentPageIndex, null, this.trackingPrefix, this.flowType, this.trackingType, this.products);
            },
        },
        beforeDestroy() {
            removeBodyScrollLock();
        },
        mounted() {
            // this will try to fix the "scroll-into-view" bug that exists in firefox on android
            if (/Android/.test(navigator.appVersion)) {
                // when selecting an input field, firefox fires the resize event
                window.addEventListener('resize', () => {
                    if (document.activeElement.tagName=='INPUT' || document.activeElement.tagName=='TEXTAREA') {
                        document.activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }
        },
        async created() {
            this.handleRecruitmentCode();

            window.onbeforeunload = () => {
                // abit hacky, but we verify that the "orderflow" element is present, if it is we set the eventhandler
                const isOrderflow = document.getElementById('orderflow') !== null || undefined;
                let isPopUpRequired = true;

                if (!isOrderflow || this.currentPageIndex === 0) isPopUpRequired = false;

                if (this.tabs && this.currentPageIndex === this.tabs.length - 1) {
                    // we want to reset the state since we completed our order
                    isPopUpRequired = false;
                    this.$store.dispatch('cart/resetState');
                    this.$store.dispatch('flow/resetState');
                    this.$store.dispatch('config/resetState');
                    this.$store.dispatch('products/resetState');
                    scrollToTop();
                }

                if (isOrderflow && isPopUpRequired) return '';
            };

            const key = this.getConfigKey();
            if (!key) return;

            const orderFlowConfig = await getOrderFlowConfig(key);
            this.$store.dispatch('config/setOrderFlowConfig', orderFlowConfig);
            await this.setAssortments();

            orderFlowConfig.flows.forEach(flow => {
                if (this.flowType === flow.type || !this.flowType) {
                    this.$store.dispatch('config/setFlowConfig', flow);
                    this.$store.dispatch('flow/setFlow', flow);
                    this.$store.dispatch('flow/setTabs', flow.pages);
                }
            });

            this.getFlow();
            trackVirtualPageView(this.tabs, this.currentPageIndex, null, this.trackingPrefix, this.flowType, this.trackingType, this.products);
            this.$store.dispatch('products/setActiveAssortmentIndex', this.getDeeplinkAssortmentIndex());

            const types = this.assortments.list[this.activeAssortmentIndex].types;
            if (types && this.users.length === 0 && this.currentPageIndex === 0) {
                // new default state for share flow is that we will have 2 family members
                types.forEach(type => addUserByUserType(type.identifier));
                if (this.flowType === 'share' && types.length === 1) types.forEach(type => addUserByUserType(type.identifier));
            }

            this.isReady = true;
        },
        methods: {
            getConfigKey() {
                const key = document.getElementById('key');

                if (key === null && this.configKey === undefined) return;
                if (key === null && this.configKey !== undefined) return this.configKey;

                const isOidcRedirect = window.location.pathname.includes('oidcredirect');

                if (this.configKey !== key.value && !isOidcRedirect) {
                    this.$store.dispatch('cart/resetState');
                    this.$store.dispatch('config/resetState');
                    this.$store.dispatch('flow/resetState');
                    resetOidcStore();
                    this.$store.dispatch('config/setKey', key.value);

                    if (!this.title) this.$store.dispatch('config/setTitle', document.title);
                } else {
                    document.title = this.title;
                }

                return this.configKey;
            },
            getFlow() {
                if (!this.currentFlow) return;

                this.makeFlowQueue(this.currentFlow.id);

                this.flowQueue.forEach(flowFromQueue => {
                    const isRestricted = !this.isCartItemsWithinBoundariesOfFlow(flowFromQueue);
                    if (isRestricted) return;

                    this.$store.dispatch('flow/setFlow', flowFromQueue);
                    this.$store.dispatch('flow/setTabs', flowFromQueue.pages);
                });
            },
            isCartItemsWithinBoundariesOfFlow(flow) {
                const cartCount = this.cartItems.length;
                return cartCount <= flow.restrictions.productsMax && cartCount >= flow.restrictions.productsMin;
            },
            makeFlowQueue(flowId) {
                if (!flowId) return;

                const flow = this.orderFlowConfig.flows.find(flow => flow.id === flowId);
                if (!flow) return;

                const flowFromQueue = this.flowQueue.find(f => f.id === flow.id);
                if (flowFromQueue) return;

                this.$store.dispatch('flow/pushToFlowQueue', flow);
                this.makeFlowQueue(flow.restrictions.fallbackFlow);
            },
            async setAssortments() {
                // This should be refactored.
                // This function is called here in order to reload the promocode whenever the orderflow changes.
                this.handleRecruitmentCode();

                const calculation = await post(this.calculateOrder, this.request);
                const assortments = calculation.assortments;
                const newHash = assortments.hash;

                this.$store.dispatch('cart/setCurrentCalculation', calculation);
                if (this.assortmentsHash === newHash) return;
                this.$store.dispatch('cart/setAssortments', assortments);
                const assortmentsList = assortments.list[this.activeAssortmentIndex];
                // if we can't find our assortmentsList we default to the first one
                const productList = (assortmentsList) ? assortmentsList.list : assortments.list[0].list;
                this.$store.dispatch('products/setProductList', productList);
            },
            /**
             * Checks if a deeplink for assortment is set.
             * Then validates if there exists an assortment at the given index
             * Returns 0 if no matching assortment is found, or if no deeplink is given.
             * Returns the correct index if a value was specififed and valid
             */
            getDeeplinkAssortmentIndex() {
                const deepLinkIdentifier = this.$route.query.tab;
                if (deepLinkIdentifier === undefined) return 0;

                const assortmentIndex = this.assortments.list.findIndex(assortment => assortment.key === deepLinkIdentifier);

                if (assortmentIndex === -1) return 0;
                return assortmentIndex;
            },
            handleRecruitmentCode() {
                const cookieCode = this.$cookie.get(recruitmentCookieName);

                if (!cookieCode) return;
                if (this.promoCode === cookieCode) return;

                this.$store.dispatch('cart/setPromoCode', cookieCode);
            },
        },
    };
</script>
